/** @file RevisionBadge: "rev N" and the live-sync state beside the puppet name. */
import type { ReactElement } from 'react';
import { useEditor } from '../state/useEditor';
import { useHeldWhile } from './useHeldWhile';

const LIVE = { open: 'connected', connecting: 'reconnecting', off: 'off' } as const;

/**
 * `rev N` is the server revision; while a gizmo or slider drag is in flight (`poseOverride`) a revision that another
 * actor sends does not move the number (it shows when the drag ends). The second word is the live socket:
 * connected, reconnecting or off.
 */
export function RevisionBadge(): ReactElement {
  const revision = useEditor((s) => s.puppet.revision);
  const pending = useEditor((s) => s.puppet.pending);
  const live = useEditor((s) => s.puppet.live);
  const dragging = useEditor((s) => s.poseOverride !== null);
  const shown = useHeldWhile(revision, dragging);
  const waiting = shown !== revision;
  return (
    <>
      <span className="p3-muted p3-mono" title={waiting ? `Revision ${String(revision)} arrived during your drag and shows when it ends` : 'Server revision'}>
        rev {shown}{pending > 0 ? ` +${String(pending)}` : ''}
      </span>
      <span className={live === 'open' ? 'p3-muted' : 'p3-error'} title="Live sync with the server and other editors" data-live={live}>{LIVE[live]}</span>
    </>
  );
}
